import React from 'react';
import { SectionDivider } from '../SectionDivider';
export interface TimelineItem {
  id: string;
  status: 'live' | 'development' | 'planned' | 'future';
  title: string;
  description: string;
  date: string;
}
interface TimelineProps {
  title?: string;
  subtitle?: string;
  items: TimelineItem[];
  'data-id'?: string;
  trainingAILabel?: string;
  showTrainingAI?: boolean;
}
export function Timeline({
  title = 'Product Roadmap',
  subtitle = `We're building AI that evolves with your business—starting with the apps you rely on most, then training sub-agents and improving the experience to unlock smarter insights.`,
  items,
  'data-id': dataId,
  trainingAILabel = 'TRAINING AI',
  showTrainingAI = true
}: TimelineProps) {
  const getStatusColor = (status: TimelineItem['status']) => {
    // All dots now use benetton-blue regardless of status
    return 'bg-benetton-blue';
  };
  const getStatusBadgeStyle = (status: TimelineItem['status']) => {
    // All badges now use benetton-blue styling regardless of status
    return 'bg-benetton-blue/15 text-benetton-blue font-semibold';
  };
  const getStatusText = (status: TimelineItem['status']) => {
    switch (status) {
      case 'live':
        return 'IN ALPHA TESTING';
      case 'development':
        return 'IN DEVELOPMENT';
      case 'planned':
        return 'PLANNED';
      case 'future':
        return 'FUTURE';
      default:
        return 'UNKNOWN';
    }
  };
  return <>
      <SectionDivider from="from-white" to="to-gray-50/30" height="h-16" data-id="element-512" />
      <section className="w-full bg-gray-50/30 py-24 px-6 relative overflow-hidden" data-id="element-513">
        {/* Yellow background gradient starting earlier */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-benetton-yellow/10 to-benetton-yellow/20 pointer-events-none" data-id="element-514"></div>
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-515">
          <div className="absolute top-[30%] right-[20%] w-[45%] h-[45%] rounded-full opacity-[0.03] blur-[150px]" style={{
          background: 'radial-gradient(circle, rgba(0,136,96,0.6) 0%, rgba(255,255,255,0) 70%)'
        }} data-id="element-516"></div>
          <div className="absolute bottom-[10%] left-[10%] w-[40%] h-[40%] rounded-full opacity-[0.025] blur-[120px]" style={{
          background: 'radial-gradient(circle, rgba(0,87,184,0.5) 0%, rgba(255,255,255,0) 70%)'
        }} data-id="element-517"></div>
        </div>
        {/* Decorative paint stroke elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none" data-id="element-518">
          <div className="absolute top-8 left-4 w-24 h-1 bg-gradient-to-r from-benetton-green to-transparent transform -rotate-12 opacity-60" data-id="element-519"></div>
          <div className="absolute top-12 left-8 w-16 h-1 bg-gradient-to-r from-benetton-red to-transparent transform -rotate-6 opacity-40" data-id="element-520"></div>
          <div className="absolute top-16 right-8 w-32 h-1 bg-gradient-to-l from-benetton-lightGreen to-transparent transform rotate-12 opacity-50" data-id="element-521"></div>
          <div className="absolute top-20 right-12 w-20 h-1 bg-gradient-to-l from-benetton-orange to-transparent transform rotate-6 opacity-30" data-id="element-522"></div>
          <div className="absolute bottom-12 left-1/4 w-28 h-1 bg-gradient-to-r from-benetton-violet to-transparent transform rotate-3 opacity-40" data-id="element-523"></div>
          <div className="absolute bottom-8 right-1/3 w-24 h-1 bg-gradient-to-l from-benetton-yellow to-transparent transform -rotate-3 opacity-50" data-id="element-524"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-525">
          <div className="mx-auto max-w-2xl sm:text-center mb-16" data-id="element-526">
            <p className="mt-2 text-3xl font-normal tracking-tight text-benetton-green sm:text-4xl font-apercu" data-id="element-527">
              {title}
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 font-jakarta" data-id="element-528">
              {subtitle}
            </p>
          </div>
          <div className="relative" data-id="element-529">
            {/* Main Timeline */}
            <div className="absolute left-1/2 transform -translate-x-0.5 w-0.5 bg-gray-200 h-full" data-id="element-530"></div>
            <div className="space-y-24" data-id="element-531">
              {items.map((item, index) => <div key={item.id} className="relative flex items-start" style={{
              height: index === items.length - 1 ? 'auto' : '96px'
            }} data-id="element-532">
                  <div className="flex flex-col items-end justify-start w-1/2 pr-8" data-id="element-533">
                    {index % 2 === 0 ? <>
                        {/* Status badge on the same side as content - with matching colors */}
                        <div className={`px-3 py-1 rounded-full text-xs mb-3 ${getStatusBadgeStyle(item.status)}`} data-id="element-534">
                          {getStatusText(item.status)}
                        </div>
                        <div className="text-heading-4 font-normal text-benetton-green mb-2 font-apercu" data-id="element-535">
                          {item.title}
                        </div>
                        <div className="text-body-sm text-gray-600 mb-4 font-jakarta" data-id="element-536">
                          {item.description}
                        </div>
                        <div className="text-sm font-semibold text-gray-500 font-jakarta" data-id="element-537">
                          {item.date}
                        </div>
                      </> : <div className="w-full h-full flex items-start justify-end" data-id="element-538">
                        {/* Empty space for odd indices */}
                      </div>}
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-4 mt-1" data-id="element-539">
                    <div className={`w-8 h-8 rounded-full ${getStatusColor(item.status)} flex items-center justify-center shadow-sm`} data-id="element-540">
                      <div className="w-3 h-3 bg-white rounded-full" data-id="element-541"></div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start justify-start w-1/2 pl-8" data-id="element-542">
                    {index % 2 === 1 ? <>
                        {/* Status badge on the same side as content - with matching colors */}
                        <div className={`px-3 py-1 rounded-full text-xs mb-3 ${getStatusBadgeStyle(item.status)}`} data-id="element-543">
                          {getStatusText(item.status)}
                        </div>
                        <div className="text-heading-4 font-normal text-benetton-green mb-2 font-apercu" data-id="element-544">
                          {item.title}
                        </div>
                        <div className="text-body-sm text-gray-600 mb-4 font-jakarta" data-id="element-545">
                          {item.description}
                        </div>
                        <div className="text-sm font-semibold text-gray-500 font-jakarta" data-id="element-546">
                          {item.date}
                        </div>
                      </> : <div className="w-full h-full flex items-start" data-id="element-547">
                        {/* Empty space for even indices */}
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      <SectionDivider from="from-gray-50/30" to="to-white" height="h-16" data-id="element-548" />
    </>;
}